import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SettingsIcon from '@mui/icons-material/Settings';
import { publicationHealth, StatusChip, formatDate, labelize } from './WorkspaceStatus';

export function EntityStatGrid({ items = [] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: `repeat(${Math.min(Math.max(items.length, 1), 4)}, minmax(0, 1fr))` }, gap: 1.5 }}>
      {items.map((item) => (
        <Paper key={item.label} variant="outlined" sx={{ p: 1.75, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          <Typography variant="h6" sx={{ mt: 0.25, overflowWrap: 'anywhere' }}>{item.value ?? '—'}</Typography>
          {item.helper ? <Typography variant="caption" color="text.secondary">{item.helper}</Typography> : null}
        </Paper>
      ))}
    </Box>
  );
}

export function ReadinessPanel({ validation, checks = [], title = 'Readiness', description }) {
  const completed = checks.filter((check) => check.complete).length;
  const errors = validation?.errors || [];
  const warnings = validation?.warnings || [];
  const valid = validation?.valid === true && errors.length === 0;
  const percent = checks.length ? Math.round((completed / checks.length) * 100) : (valid ? 100 : 0);
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography component="h2" variant="subtitle1">{title}</Typography>
            {description ? <Typography variant="body2" color="text.secondary">{description}</Typography> : null}
          </Box>
          <StatusChip status={valid ? 'ready' : errors.length ? 'needs_attention' : 'draft'} label={valid ? 'Ready' : errors.length ? `${errors.length} blockers` : 'In progress'} />
        </Stack>
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">{completed} of {checks.length} setup checks complete</Typography>
            <Typography variant="caption" color="text.secondary">{percent}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={percent} aria-label={`${percent}% ready`} sx={{ height: 8, borderRadius: 8 }} />
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
          {checks.map((check) => (
            <Stack component="li" key={check.key || check.label} direction="row" spacing={1} alignItems="flex-start">
              {check.complete ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
              <Box>
                <Typography variant="body2">{check.label}</Typography>
                {check.help ? <Typography variant="caption" color="text.secondary">{check.help}</Typography> : null}
              </Box>
            </Stack>
          ))}
        </Box>
        {errors.length ? (
          <Alert severity="error">
            <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
              {errors.map((issue) => <li key={`${issue.code}-${issue.field}`}><Typography variant="body2">{issue.message}</Typography></li>)}
            </Box>
          </Alert>
        ) : null}
        {warnings.length ? <Alert severity="warning">{warnings.length} recommendation{warnings.length === 1 ? '' : 's'} remain before publishing.</Alert> : null}
      </Stack>
    </Paper>
  );
}

export function PublicationCard({ publication, onManage, compact = false }) {
  const health = publicationHealth(publication);
  const managedFields = Array.isArray(publication.managed_field_groups) ? publication.managed_field_groups : [];
  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%', borderColor: health === 'needs_attention' ? 'error.main' : undefined }}>
      <Stack spacing={1.5} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" variant="subtitle2" noWrap title={publication.connection?.display_name || publication.provider}>
              {publication.connection?.display_name || labelize(publication.provider)}
            </Typography>
            <Typography variant="caption" color="text.secondary">{labelize(publication.provider)}</Typography>
          </Box>
          <StatusChip status={health} />
        </Stack>
        <Box>
          <Typography variant="caption" color="text.secondary">Remote listing</Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{publication.external_id || 'Not linked yet'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Managed fields</Typography>
          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
            {managedFields.length ? managedFields.map((field) => <Chip key={field} size="small" variant="outlined" label={labelize(field)} />) : <Typography variant="body2">Observed only</Typography>}
          </Stack>
        </Box>
        {(publication.collections || []).length ? (
          <Typography variant="caption" color="text.secondary">Collections: {publication.collections.map((item) => item.name).join(', ')}</Typography>
        ) : null}
        <Typography variant="caption" color={publication.last_error ? 'error' : 'text.secondary'} sx={{ overflowWrap: 'anywhere' }}>
          {publication.last_error || (publication.last_verified_at ? `Verified ${formatDate(publication.last_verified_at)}` : 'Remote state has not been verified yet')}
        </Typography>
        {onManage ? <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => onManage(publication)} sx={{ mt: 'auto', alignSelf: 'flex-start' }}>Review and plan</Button> : null}
      </Stack>
    </Paper>
  );
}
